import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AuthGate from '../../components/AuthGate/AuthGate';
import Button from '../../components/Button/Button';
import Input from '../../components/Input/Input';
import { useThemeColor } from '../../hooks/useThemeColor';
import authService from '../../services/auth.service';

const ResetPassword: React.FC = () => {
  useThemeColor('#6B21A8');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [tokenState, setTokenState] = useState<'loading' | 'valid' | 'invalid'>('loading');
  const [formData, setFormData] = useState({ password: '', confirm_password: '' });
  const [formErrors, setFormErrors] = useState({ password: '', confirm_password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setTokenState('invalid');
      return;
    }
    authService.validateResetToken(token)
      .then(() => setTokenState('valid'))
      .catch(() => setTokenState('invalid'));
  }, [token]);

  const validate = (): boolean => {
    const errors = { password: '', confirm_password: '' };
    if (!formData.password) {
      errors.password = 'Le mot de passe est obligatoire';
    } else if (formData.password.length < 8) {
      errors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre';
    }
    if (!formData.confirm_password) {
      errors.confirm_password = 'La confirmation est obligatoire';
    } else if (formData.password !== formData.confirm_password) {
      errors.confirm_password = 'Les mots de passe ne correspondent pas';
    }
    setFormErrors(errors);
    return !errors.password && !errors.confirm_password;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
    setError('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setError('');
    try {
      await authService.resetPassword({ token, ...formData });
      navigate('/login', { state: { passwordReset: true } });
    } catch (err: any) {
      const msg = err.response?.data?.error?.message;
      setError(msg || 'Une erreur est survenue. Le lien est peut-être expiré.');
    } finally {
      setIsLoading(false);
    }
  };

  const panel = {
    eyebrow: 'Compte',
    title: <>Nouveau <em>mot de passe.</em></>,
    lead: '8 caractères, une majuscule, un chiffre.',
  };

  if (tokenState === 'loading') {
    return (
      <AuthGate panel={panel}>
        <p className="auth-sub">Vérification du lien…</p>
      </AuthGate>
    );
  }

  if (tokenState === 'invalid') {
    return (
      <AuthGate panel={panel}>
        <p className="auth-kicker">Lien</p>
        <h1 className="auth-h">Lien invalide</h1>
        <p className="auth-sub">Expiré ou déjà utilisé.</p>
        <div className="auth-alert auth-alert--error">
          Demandez un nouveau lien.
        </div>
        <div className="auth-foot">
          <p>
            <Link to="/forgot-password">Nouveau lien</Link>
            {' · '}
            <Link to="/login">Connexion</Link>
          </p>
        </div>
      </AuthGate>
    );
  }

  return (
    <AuthGate panel={panel}>
      <p className="auth-kicker">Compte</p>
      <h1 className="auth-h">Nouveau mot de passe</h1>
      <p className="auth-sub">Choisissez-en un solide.</p>

      {error && <div className="auth-alert auth-alert--error">{error}</div>}

      <form className="auth-form" onSubmit={handleSubmit}>
        <Input
          label="Nouveau mot de passe"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          error={formErrors.password}
          placeholder="Min. 8 caractères"
          required
          fullWidth
          autoComplete="new-password"
        />
        <Input
          label="Confirmer"
          type="password"
          name="confirm_password"
          value={formData.confirm_password}
          onChange={handleChange}
          error={formErrors.confirm_password}
          placeholder="Répétez le mot de passe"
          required
          fullWidth
          autoComplete="new-password"
        />
        <Button type="submit" variant="primary" size="large" fullWidth loading={isLoading}>
          Réinitialiser
        </Button>
      </form>

      <div className="auth-foot">
        <p><Link to="/login">Retour à la connexion</Link></p>
      </div>
    </AuthGate>
  );
};

export default ResetPassword;
